import { createContext, useContext } from 'react';

import type { LumioNudge } from './components/LumioChat/endpoint';

/**
 * The live proactive nudge, provided by `MainLayout` (from `useLumioNudges`) and
 * consumed by the Lumio panel so it can DOCK the nudge inside its briefing (the
 * "Noticed" card) instead of only floating it as a toast near the launcher. A
 * context — not props — so it reaches the panel through every surface that renders
 * it (the docked column and the drawer) without threading it down by hand.
 *
 * When a surface docks the nudge, `MainLayout` suppresses the floating toast (see
 * its render), so the same nudge is never shown twice. `resolve` records the
 * owner's response and clears the nudge (the one source of truth is the hook).
 */
export type NudgeOutcome = 'clicked' | 'dismissed' | 'ignored';

export interface LumioNudgeBus {
  /** The nudge to dock, or `null` when there is nothing to show. */
  nudge: LumioNudge | null;
  /** Record the outcome and clear the nudge. */
  resolve: (outcome: NudgeOutcome) => void;
  /**
   * Report that this surface can now dock a nudge (`1`), or can no longer do so
   * (`-1`). A panel can only dock into its briefing, so it stops being a surface
   * the moment a conversation starts or the inbox opens, and `MainLayout` has no
   * other way to know that.
   *
   * It matters because asking is the only thing that costs anything. The backend
   * spends a notice when it EMITS one: the dedupe key, the 180s gap and one of
   * three per-session slots are written before the response is sent
   * (nudges.py:742-833), and no outcome we report afterwards returns any of it
   * (record_outcome returns early for every id but `setup_incomplete`). So a
   * notice we request and cannot show is simply lost. Counting live surfaces is
   * what lets the layout avoid asking, and the floating toast is what keeps a
   * surface available so that gate never starves a viewport.
   */
  reportSurface: (delta: number) => void;
}

/** Inert default so the Lumio components still mount standalone (CTs / stories). */
export const LumioNudgeContext = createContext<LumioNudgeBus>({
  nudge: null,
  resolve: (): void => undefined,
  reportSurface: (): void => undefined,
});

/** Access the docked nudge (+ its resolver) from under `MainLayout`. */
export const useLumioNudge = (): LumioNudgeBus => useContext(LumioNudgeContext);
